import { useEffect, useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

type Ticket = {
  id: string;
  ticket_number: number;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  business_name: string | null;
  urgency: string | null;
  robot_model: string | null;
  internal_notes: string | null;
  created_at: string;
};

const STATUSES = ["open", "in_progress", "waiting_customer", "resolved", "closed"];

const AdminSupportTickets = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("support_tickets")
      .select("*")
      .order("created_at", { ascending: false });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setTickets((data ?? []) as Ticket[]);
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("support_tickets").update({ status: status as any }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Status updated");
    load();
  };

  const saveNotes = async () => {
    if (!selected) return;
    const { error } = await supabase.from("support_tickets").update({ internal_notes: notes }).eq("id", selected.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Notes saved");
    setSelected(null);
    load();
  };

  return (
    <>
      <SEO title="Support Tickets — Admin | Neil AI" description="Admin support ticket dashboard." canonical="https://www.neilai.ai/admin/support-tickets" />
      <div className="min-h-screen bg-[#05070A] text-white pb-24">
        <Navigation />
        <section className="pt-32 md:pt-40 pb-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl md:text-4xl font-bold">Support Tickets</h1>
              <Button variant="outline" onClick={load}>Refresh</Button>
            </div>

            {loading ? (
              <div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
            ) : (
              <Card className="bg-[#0A1828]/60 border-primary/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#05070A] text-slate-400 text-xs uppercase">
                      <tr>
                        <th className="text-left p-3">#</th>
                        <th className="text-left p-3">Customer</th>
                        <th className="text-left p-3">Subject</th>
                        <th className="text-left p-3">Category</th>
                        <th className="text-left p-3">Priority</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Created</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((t) => (
                        <tr key={t.id} className="border-t border-primary/10 hover:bg-[#05070A]/50">
                          <td className="p-3 font-mono text-[#00F5A0]">#{t.ticket_number}</td>
                          <td className="p-3">
                            <div>{t.guest_name || "—"}</div>
                            <div className="text-xs text-slate-400">{t.guest_email}{t.business_name ? ` · ${t.business_name}` : ""}</div>
                          </td>
                          <td className="p-3 max-w-xs truncate">{t.subject}</td>
                          <td className="p-3"><Badge variant="outline">{t.category}</Badge></td>
                          <td className="p-3"><Badge>{t.priority}</Badge></td>
                          <td className="p-3">
                            <Select value={t.status} onValueChange={(v) => updateStatus(t.id, v)}>
                              <SelectTrigger className="h-8 w-40 bg-[#05070A] border-primary/30"><SelectValue /></SelectTrigger>
                              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="p-3 text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</td>
                          <td className="p-3"><Button size="sm" variant="outline" onClick={() => { setSelected(t); setNotes(t.internal_notes || ""); }}>View</Button></td>
                        </tr>
                      ))}
                      {tickets.length === 0 && (
                        <tr><td colSpan={8} className="p-10 text-center text-slate-400">No tickets yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {selected && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setSelected(null)}>
                <Card className="bg-[#0A1828] border-primary/30 max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
                  <h2 className="text-xl font-bold mb-1">Ticket #{selected.ticket_number}</h2>
                  <p className="text-sm text-slate-400 mb-4">{selected.guest_name} · {selected.guest_email}{selected.guest_phone ? ` · ${selected.guest_phone}` : ""}</p>
                  <h3 className="font-semibold mb-1">{selected.subject}</h3>
                  <p className="text-sm text-slate-300 whitespace-pre-wrap mb-4">{selected.description}</p>
                  <label className="text-sm font-semibold">Internal notes</label>
                  <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#05070A] border-primary/30 mt-2" />
                  <div className="mt-4 flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
                    <Button onClick={saveNotes} className="bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A]">Save</Button>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};
export default AdminSupportTickets;
